/* Purpose: This component displays all the data and files for any loan.
 * Props: ClientID (str)
 *        LoanID (str)
 *        loanBasicInfo (map)
 *        credentials (map)
 *        handleReturnToLoanList (func)
 *        refreshAvailableOptions (func)
 *        LoanList (list of maps)
 * Author: R. Markowitz (April 2025)
 * Dev Notes: 
*/

import React, {useState, useEffect} from 'react';
import {Alert, Container, Nav, Col, Row, ProgressBar} from 'react-bootstrap';
import PageTitle from '../../Components/PageTitle';
import {refiInfo, fixAndFlipInfo, refiFiles, purchaseFiles, fixAndFlipFiles, purchaseInfo} from '../../Data/RequiredLoanData';
import { validateField } from '../../Data/Regex';
import { MAX_FILE_SIZE } from '../../Data/MaxData';
import LoanFiles from '../../Components/LoanFiles';
import ScopeOfWorkForm from '../../Components/SOW';
import LoanInfoForm from '../../Components/LoanInfo';

const LoanTemplate = (props) => {
    const LoanID = props.LoanID;
    const ClientID = props.ClientID;
    const loanBasicInfo = props.loanBasicInfo;
    const handleReturnToLoanList = props.handleReturnToLoanList;
    const refreshAvailableOptions = props.setLoanFileChange;
    const credentials = props.credentials;
    const [loan, setLoan] = useState({"LoanID": LoanID, "LoanType": "LOADING..."});
    const [loanInfoList, setLoanInfoList] = useState();
    const [loanFileList, setLoanFileList] = useState();
    const [loanRequInfoList,setLoanRequInfoList] = useState([]);
    const [loanRequFileList,setLoanRequFileList] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewFileCache, setPreviewFileCache] = useState([]);
    const [clickedSave, setClickedSave] = useState(true);
    const [saveBtnText, setSaveBtnText] = useState("Save");
    const [loadingLoanData, setLoadingLoanData] = useState(true);
    const [readyForDB, setReadyForDB] = useState(false);
    const [isSavingFile, setIsSavingFile] = useState(false); // used to trigger save to dynamodb
    const [isUploadingFile, setIsUploadingFile] = useState([]); // a list of indexes of files that are in the process of being uploaded
    const [clickedRemove, setClickedRemove] = useState("");
    // const [clickedPreview, setClickedPreview] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [stopSave, setStopSave] = useState(false);
    const [tab, setTab] = useState("LoanInfo");
    let allLoans = props.LoanList || [];

    useEffect(() => {
      window.scrollTo({top: 0, left: 0, behavior: 'smooth' });
    }, [])

    // Get a list of all the loans except the current loan being viewed
    let currentLoanIndex = allLoans.findIndex((loan) => loan.LoanID == LoanID);
    if (allLoans.length == 1 || (allLoans.length - 1) == currentLoanIndex){
      allLoans = [...allLoans.slice(0,currentLoanIndex)]
    }else{
      allLoans = [...allLoans.slice(0,currentLoanIndex),
                  ...allLoans.slice(currentLoanIndex + 1, allLoans.length)
            ]
    }

    // Fetch Loan Info from database and set the module variables
    useEffect(() => {
      const getLoanInfo = async () => {
          setLoadingLoanData(true);
          try {
              const response = await fetch(`https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/getLoanInfo`, {
                "method": "POST",
                "body": JSON.stringify({
                  "ClientID":ClientID,
                  "LoanID": LoanID,
                  "credentials": credentials
                })
              });
              if (!response.ok) {
                  setLoan([{"LoanID": "ERROR", "LoanType": "ERROR"}]);
                  console.log("Error loading loan info. Response not ok.");
              }else{
                  const data = await response.json();

                  if (data && !data == []) {
                          setLoan({...data});
                          setLoanInfoList(data["LoanInfo"]);
                          setLoanFileList(data["LoanFiles"]);  
                        try{
                          // set loanRequInfoList according to the loan type. This variable is for creating the form fields
                          let info = [];
                          let files = [];
                          let tempSelectedFiles = {};
                          if (data.LoanType == "Refinance") {
                            info = refiInfo;
                            files = refiFiles;
                          } else if (data.LoanType == "Purchase"){
                              info = purchaseInfo;
                              files = purchaseFiles;

                          } else if (data.LoanType == "Fix and Flip") {
                              info = fixAndFlipInfo;
                              files = fixAndFlipFiles;
                          } else {
                              console.log("Loan Type not Supported. Loan Type  = ", data.LoanType);
                          }

                          info = info.map((field) => ({
                            ...field, 
                            value: data.LoanInfo[field.fieldName]? data.LoanInfo[field.fieldName]: "",
                          }))
                          files = files.map((file) => ({
                            ...file, 
                            value: data.LoanFiles[file.fileName]? data.LoanFiles[file.fileName]: [],
                          }))
                    
                          setLoanRequInfoList([...info]);
                          setLoanRequFileList([...files]);
                          files.map((file) => {
                            Object.assign(tempSelectedFiles,{[file.fileName]:"Uploaded File(s)"})
                          })
                          setSelectedFiles(tempSelectedFiles)
                        }catch (err){
                            console.error("Line 116, There was an error setting info and file vars. ", err)
                            setLoan({"LoanID": "ERROR", "LoanType":"ERROR"})
                          }

                } else {
                    setLoan([{"LoanID": "ERROR", "LoanType":"ERROR"}]);
                    console.log("Data empty.");
                }
              }
          } catch (error) {
              setLoan([{"LoanID": "Something went wrong when tring to access your current loans", "LoanType": "ERROR"}]);
              console.error("There was an error getting the loan information. Error: ", error)
          }
      }
      getLoanInfo();
    }, [clickedSave])

    // when loan data is set and state updates, setloading loan data to false
    // set the "getFromOptions" for each file in loanRequFiles to the list of other loans that have the file
    // "getFromOptions" is a list of bootstrap <Dropdown.Item> elements - one for each loan with the desired file
    useEffect (() => {
      setLoadingLoanData(false)
      let tempLoanRequFileList = [...loanRequFileList]

      loanRequFileList.forEach((file, fileIndex) => {
        var availableOptions = []
        allLoans.forEach((loan2, loan2Index) => {
          if (loan2['UploadedFiles'].includes(file.fileName)){
              // ACH files only available type = type or loan.LoanType = Purch or Fix and Flip and loan2.LoanType = Purch or Fix and Flip
              if (file.fileName !== "ACH" || (loan.LoanType == loan2.LoanType || (loan.LoanType == "Refinance" && loan2.LoanType == "Fix and Flip") || (loan.LoanType == "Fix and Flip" && loan2.LoanType == "Refinance"))){
                availableOptions.push(loan2.LoanID)
              }
          }
        })
        if (availableOptions.length == 0){
          availableOptions.push("There are no loans with this file on record.")
        }
        tempLoanRequFileList[fileIndex] = {...tempLoanRequFileList[fileIndex],
                              getFromOptions: [...availableOptions]
        }
      })

      setLoanRequFileList(tempLoanRequFileList)

    }, [loan])


    // When text is entered into any of the form control boxes, save the inputed value to loanInfoList[fieldName][value]
    // This will also make the inputed value appear in the form contol
    const handleTextInfoChange = (index, event) => {
      setReadyForDB(false);
      setClickedSave(false);
      const updatedLoanInfoList = [...loanRequInfoList];

      // Update the specific item at the given index
      updatedLoanInfoList[index]= {
        ...updatedLoanInfoList[index],
        value: event.target.value,
      };

      setLoanRequInfoList(updatedLoanInfoList);
    };

    // When the user clicks out of a text box, the text will be validated
    const onBlur = ((item, index) => {
      let isValid = true;
      if (item.value !== ""){
        isValid = validateField(item)
      }      
      let tempLoanRequInfoList = [...loanRequInfoList]
      tempLoanRequInfoList[index] = {...tempLoanRequInfoList[index],
                    "isValid":isValid,
                    "value": item.value.trim()
                  };
      setLoanRequInfoList(tempLoanRequInfoList)
      

    });

    
    // When a radio button is clicked, save the selected value to loanInfoList[fieldName][value]
    const handleRadioChange = (index, event, option) => {
      setReadyForDB(false);
      setClickedSave(false);
      const updatedLoanInfo = [...loanRequInfoList];

      // Update the specific item at the given index
      updatedLoanInfo[index] = {
        ...updatedLoanInfo[index],
        value: option,
      };

      // If the affected field was "property type" add fields if necesary based on chosen value
      if (event.target.name === "Property Type"){
        // If single family is chosen, disable the number of units field
        // If any other option, enable number of units field
        if(option === "Single Family"){
          updatedLoanInfo[index + 1] = {
            ...updatedLoanInfo[index + 1],
            state: "disabled", value: "N/A"
           }
           updatedLoanInfo[index + 2] = {
            ...updatedLoanInfo[index + 2],
            state: "disabled", value: "N/A"
           }
        }else {
          updatedLoanInfo[index + 1] = {
            ...updatedLoanInfo[index + 1], 
            state: "", value: ""
          }

          // if condo option was chosen, enable HOA, if anything else, disable HOA
          if(option === "Condo"){
            updatedLoanInfo[index + 2] = {
              ...updatedLoanInfo[index + 2],
              state: "", value:""
             }
          }else {
            updatedLoanInfo[index + 2] = {
              ...updatedLoanInfo[index + 2], 
              state: "disabled", value: "N/A"
            }
          }
      }};
    
      setLoanRequInfoList(updatedLoanInfo);
    }

    // When a file is added, save the file to s3 and the filepath to dynamodb and loanRequFileList[index][value]
    const handleFileChange = async (index, event) => {
      let tempUploadingFileList = [isUploadingFile];
      tempUploadingFileList.push(index);
      setIsUploadingFile(tempUploadingFileList);
      event.preventDefault();
      const updatedLoanFileList = [...loanRequFileList];

      let uploadedFiles = []
      let filePaths = []
      for (let i = 0; i < event.target.files.length; i++){
        uploadedFiles.push(event.target.files[i]);
      };
      uploadedFiles.forEach((file) => {
        let fullName = ClientID + "/" + LoanID + "_" + updatedLoanFileList[index].fileName + "_" + file.name
        if (loanFileList[updatedLoanFileList[index].fileName] && loanFileList[updatedLoanFileList[index].fileName].includes(fullName)){
          alert("The file " + fullName.split("/")[1] + " already exists and will not be added again.")
        }else{
          filePaths.push(fullName);
        }
      });

      // Remove any empty filepaths
      filePaths = filePaths.filter((filePath) => filePath)

      // Make sure that there will not be more than 10 files uploaded for any field
      let totalFileCount = updatedLoanFileList[index].value.length + filePaths.length

      if (totalFileCount > 10){
        alert("You can only upload up to ten files for this field.")
        tempUploadingFileList = [];
        isUploadingFile.forEach((uploadingIndex) => {
          if (index != uploadingIndex){
            tempUploadingFileList.push(index);
          }
        })
        setIsUploadingFile(tempUploadingFileList)
      
      }else if (filePaths.length > 0){
        // Using a presigned url (urlData.url), save the file to s3
        const handleUploadToS3 = async (file, urlData) => {
            const formData = new FormData();
            
            Object.keys(urlData.fields).forEach(key => {
              formData.append(key, urlData.fields[key]);
            });
            formData.append('file', file);

            const response = await fetch(urlData.url, {
              method: 'POST',
              body: formData,
            });
        
            return response;
        }
    
        // For each file that was uploaded
        
        for (let i = 0; i < uploadedFiles.length; i++){
          let uploadedFile = uploadedFiles[i];
          if (uploadedFile.size <= MAX_FILE_SIZE){
            
            let filePath = filePaths[i];  

            try {
              // Get the pre-signed URL from the Lambda function
              const presignedResponse = await fetch('https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/saveFileToS3',{
                "method": "POST",
                "body": JSON.stringify({
                    "filePath": filePath,
                    "credentials": credentials
                }) 
              });
              
              if (presignedResponse.ok){
                const presignedData = await presignedResponse.json();
                try{
                  const uploadResponse = await handleUploadToS3(uploadedFile, presignedData);
                  const isUploaded = uploadResponse.ok
                  if (isUploaded) {
                      console.log("File uploaded successfully to S3!")

                      // Put the file in the preview file cache
                      const url = URL.createObjectURL(uploadedFile);
                      setPreviewFileCache([...previewFileCache, {url, name:filePath}])

                      // If file hasFromPortalOption = true, refresh the available options (so the uploaded file will appear by other loans)
                      if (loanRequFileList[index].hasFromPortalOption == "yes"){
                        console.log("Refreshing Options...")
                        refreshAvailableOptions(true);
                      }
                  }else{
                    const error = await uploadResponse.json()
                    alert(error);

                  }
                }catch (error){
                    console.error(error)
                    alert("Something went wrong while trying to upload the file.    ", error);
                    filePaths.splice(i, 1);
                }
              }else{
                const error = await presignedResponse.json()
                console.log("Response not ok. ", error)
                alert(error)
                filePaths.splice(i, 1);
              }
              

            }catch (error){
              console.error("something went wrong when getting the presigned url. ", error)
              alert("Something went wrong while trying to upload the file.   ", error)
              filePaths.splice(i, 1);
            }
          
          }else{
              alert("Error uploading " + filePaths[i] + ". File too large. Try uploading a file less than 10 MB.")
              filePaths.splice(i,1);
          }  

        if (filePaths.length > 0){
          // If this is the first uploaded file, set the filepath for selectedFiles[fileName]
          let fileName = updatedLoanFileList[index].fileName;
          if (updatedLoanFileList[index].value.length == 0){
            let tempSelectedFiles = {...selectedFiles, [fileName]:filePaths[0]};
            setSelectedFiles(tempSelectedFiles);
          }

          
          updatedLoanFileList[index]= {
            ...updatedLoanFileList[index],
            value: [...updatedLoanFileList[index].value, ...filePaths], 
            file: [updatedLoanFileList[index].file, ...uploadedFiles]
          };

          // Save the uploaded file path in dynamodb
          const updateFileName = updatedLoanFileList[index].fileName
          setLoanFileList({...loanFileList, 
              [updateFileName] : [...updatedLoanFileList[index].value]
              })

          // When is isSavingFile is true, saveToDB useEffect is triggered
          setIsSavingFile(true);
          setLoanRequFileList(updatedLoanFileList);
        }
      }
      }else{
        console.log("There are no files to upload")
      }

      tempUploadingFileList = [];
          isUploadingFile.forEach((uploadingIndex) => {
            if (index != uploadingIndex){
              tempUploadingFileList.push(index);
            }
          })
          setIsUploadingFile(tempUploadingFileList)
    };

    // Remove a file when the remove button sets clicked remove with the index of the clicked field
    useEffect(() => {
      const removeFile = async (fieldIndex) => {
        const filePath = selectedFiles[loanRequFileList[fieldIndex].fileName]
        const updatedFileList = [...loanRequFileList]
        const fileIndex = updatedFileList[fieldIndex].value.indexOf[filePath];

        try {
            const response = await fetch('https://6gps7s68d9.execute-api.us-east-1.amazonaws.com/deleteFileInS3', {
              "method":"POST",
              "body":JSON.stringify({
                "filePath":filePath,
                "credentials": credentials
              })
            })

            if (response.ok){
              console.log("File successfully removed!")

              let file;
              if (!updatedFileList[fieldIndex].file || updatedFileList[fieldIndex].file.length == 0){
                file = [];
              }else{
                let tempFile = updatedFileList[fieldIndex].file
                tempFile.pop(tempFile[fileIndex]);
                file = [tempFile];
              }

              let value;
              if (updatedFileList[fieldIndex].value.length == 0){
                value = [];
              }else{
                let tempValue = updatedFileList[fieldIndex].value
                tempValue.pop(tempValue[fileIndex]);
                value = [...tempValue];
              }

              updatedFileList[fieldIndex] = {
                ...updatedFileList[fieldIndex], 
                file: file,
                value: value
              };

              // Reset the file path in dynamodb
              const updateFieldName = updatedFileList[fieldIndex].fileName
              setLoanFileList({...loanFileList, 
              [updateFieldName] : value
                  })
              setIsSavingFile(true);

              // Remove the file from file cache
              const cacheIndex =  previewFileCache.findIndex(file => file.name === filePath);

              // Check if the index is valid (i.e., the object exists in the array)
              if (cacheIndex !== -1) {
                // Remove the object from the array
                previewFileCache.splice(cacheIndex, 1);
              }

              // If file hasFromPortalOption = true, refresh the available options (so the uploaded file will appear by other loans)
              if (loanRequFileList[fieldIndex].hasFromPortalOption == "yes"){
                console.log("Refreshing Options...")
                refreshAvailableOptions(true);
              }

            } else {
                alert('Failed to remove file from S3.');
            }

        }catch (error){
            console.error(error)
            alert("Something went wrong while removing the file, "+ error);
            
        }
      
        setLoanRequFileList(updatedFileList);
      };

      if (loanRequFileList.length > 0 && clickedRemove !== ""){
        const index = clickedRemove
        let isSelectedFilesUpdated = ((prevState) => prevState !== selectedFiles);
        if (isSelectedFilesUpdated && selectedFiles[loanRequFileList[index].fileName] != "Uploaded File(s)"){
          removeFile(index);
          setClickedRemove("");
          setSelectedFiles({...selectedFiles, [loanRequFileList[index].fileName]:"Uploaded File(s)"})

        }else if(selectedFiles[loanRequFileList[index].fileName] == "Uploaded File(s)"){
          alert("No file is selected. Please choose a file before trying to delete.")     
          setClickedRemove("")
        }else{
          setClickedRemove("")
        }
      }
    }, [clickedRemove, selectedFiles]);

    // When "Get From Another Loan" is clicked, give a drop down of other loans that the file can be loaded from.
    // Load the filePath and save it to this loan's dynamodb
    const handleGetFromOtherLoan = async (item, index, loan, comingFromModal = false) => {
        let tempUploadingFileList = [...isUploadingFile]
        if (item.maxUploads == "multiple" && !comingFromModal){
            setShowModal(loan);
        }else{
          tempUploadingFileList.push(index);
          setIsUploadingFile(tempUploadingFileList);
          let filePath;

          if (comingFromModal){
            filePath = loan // loan is really the fileName when coming from Modal
          }else{
            // Get the loan file info
            try {
              const params = {ClientID:ClientID, LoanID: loan, credentials: credentials}
              const response = await fetch(`https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/getLoanInfo`, {
                "method": "POST",
                "body": JSON.stringify(params)
              });
              if (!response.ok) {
                  alert("Something went wrong when trying to get the file from ", loan, ". File not loaded.")
              }else{
                  const data = await response.json();
                  if (data && !data == []){
                  // Save the filepath from loan to item.value
                  filePath = data['LoanFiles'][item.fileName][0];
                  }else{
                    console.error("An error occured while trying to get a file from another loan. Data empty.")
                    alert("An error occured while trying to get a file from another loan.")
                  }
              }
            }catch (error){
              console.error("An error occured while trying to get a file from another loan. Error: ", error)
              alert("An error occured while trying to get a file from another loan.")
            }
        }

        if(filePath){
          // Increment the reference count for the file in dynamodb
          try{
            const params = {
              method: "POST",
              body: JSON.stringify({
                'ClientID': ClientID,
                'filePath': filePath,   
              })
            }
            const response = await fetch("https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/updateFileRefCount", params)
            if (!response.ok){
              alert("Something went wrong when trying to increment the reference count for this file.");
            }
          }catch (e){
            alert("Something went wrong when trying to increment the reference count for this file.");
            console.error("Something went wrong when incrementing the reference count for this file.");
          }

          let tempLoanRequFileList = [...loanRequFileList];
          tempLoanRequFileList[index] = {
                ...tempLoanRequFileList[index],
                value: [...tempLoanRequFileList[index]['value'], filePath]
          }

          // Save the uploaded file path in db ready fileList
          setLoanFileList({...loanFileList, 
              [item.fileName] : [...tempLoanRequFileList[index]['value']]
              })

          // When is isSavingFile is true, saveToDB useEffect is triggered
          setIsSavingFile(true);
          setLoanRequFileList(tempLoanRequFileList);
        }
      }
      tempUploadingFileList = [];
        isUploadingFile.forEach((uploadingIndex) => {
          if (index != uploadingIndex){
            tempUploadingFileList.push(index);
          }
        })
        setIsUploadingFile(tempUploadingFileList)
    };


    // When the save button is clicked, save all the inputed values to loanInfoList and set readyForDB to trigger the save to DynamoDB useEffect
    const handleClickSave = async () => {
      setSaveBtnText("Saving...")

      // Get all the values from loanRequ data lists and save them as info object with fieldName:value
      let tempLoanInfoList = {};
      loanRequInfoList.forEach((field, index) => {
        if ((field.fieldType === "text" && (field.value == "" || validateField(field))) || field.fieldType != "text"){
          loanRequInfoList[index] =  {...loanRequInfoList[index], isValid: true}
          Object.assign(tempLoanInfoList, {[field.fieldName]: field.value});
          
        }else{
          loanRequInfoList[index] =  {...loanRequInfoList[index], isValid: false}
        }
        });

      // If any of the fields are invalid, do not save.
      let hasInvalidFields = false
      loanRequInfoList.forEach((field, index) => {
        if (!field.isValid){
          hasInvalidFields = true;
        }
      })

      if (hasInvalidFields){
        setStopSave(true);
        setSaveBtnText("Save")
      }else{
        setStopSave(false);
        setLoanInfoList({...tempLoanInfoList});
        setReadyForDB(true);
      }

    }

    // When readyForDB or isSavingFile is true, save the update to dynamodb
    useEffect (() => {
      const saveToDB = async (updateCategory, updateList) => {

        // Save the updated loan data to the database
        const params = {
          method: "POST",
          body: JSON.stringify({
              "ClientID": ClientID,
              "LoanID": LoanID,
              "updateCategory": updateCategory,
              "updatedInfo": updateList,
              "credentials": credentials
          })
        }

        const updateUrl = "https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/updateLoanInfo";

        try {
          const response = await fetch(updateUrl, params);

          if (response.ok) {
            if (readyForDB){
              setClickedSave(true);
              setSaveBtnText("Save")
            }else{
              setIsSavingFile(false);
            }
            alert("Your loan was successfully updated!");           
              
          } else {
              let errorMsg = response.statusText
              if (response.status == 400){
                errorMsg = await response.json()
              }
              setSaveBtnText("Save")
              setIsSavingFile(false);
              alert('Something went wrong while saving your changes. ' + errorMsg);
              console.error('Error:', response.statusText);
          }
        } catch (error) {
          setSaveBtnText("Save")
          setIsSavingFile(false);
          alert('Something went wrong while saving your changes. Error: ' + error);
          console.error('Error:', error);
          }
      }


      if (readyForDB){
        console.log("Saving Loan Info Changes to DB...")
        saveToDB("Loan Info", loanInfoList);
      }else if (isSavingFile){
        console.log("Saving File List Changes to DB...")
        saveToDB("Loan Files", loanFileList);
      }
      setReadyForDB(false);
      setIsSavingFile(false);
      
    }, [readyForDB, isSavingFile])

    const handleBack = (currentLocation) => {
      if (!clickedSave){
        alert("Your information has not been saved! Please save before leaving this page.")
      }else{
        if (currentLocation == "LoanInfo"){
          handleReturnToLoanList()
        }else{ // currentLocation == ScopeOfWork
          setTab("LoanInfo")
        }
      }
    };

    const handleNext = (currentLocation) => {
      if (!clickedSave){
        alert("Your information has not been saved! Please save before leaving this page.")
      }else{
        if (currentLocation == "LoanInfo"){
          setTab(loan.LoanType == "Fix and Flip"?"ScopeOfWork":"LoanFiles")
        }else{ // currentLocation == ScopeOfWork
          setTab("LoanFiles")
        }
      }
    }
    
    return(
      
        <div> 
            <PageTitle pageName={loan.LoanID + '          (' + loan.LoanType+')'} />
            {loanBasicInfo.Status && (<Col className = "m-3">
                <ProgressBar label = {loanBasicInfo.Status}>
                    <ProgressBar variant="light" now={25} key={1} label = "Intake"/>
                    <ProgressBar variant="secondary" now={loanBasicInfo.Status != "Intake"?25:0} key={2} label = {loanBasicInfo.Status == "Processing"? loanBasicInfo.Status: "Processed"}/>
                    <ProgressBar variant="info" now={(loanBasicInfo.Status == "Underwriting" || loanBasicInfo.Status == "Approved")?25:0} key={3} label = {loanBasicInfo.Status == "Underwriting"? loanBasicInfo.Status: "Underwritten"}/>
                    <ProgressBar variant="success" now={loanBasicInfo.Status == "Approved"?25:0} key={4} label = {loanBasicInfo.Status == "Approved"? loanBasicInfo.Status: "Approved"}/>
                </ProgressBar>
            </Col>)}
            <p className='text-center mb-4'>
              <strong>Fill out the form and upload the necessary files. Make sure to SAVE before leaving this page. All fields can be changed after saving.</strong>
              <br />
              **Only files with the following extensions can be uploaded: .pdf, .jpg, .png**
            </p>

            {(loanBasicInfo.ClientFeedback && loanBasicInfo.ClientFeedback.General && loanBasicInfo.ClientFeedback.General.length > 0) && (
              <p className = 'client-feedback'>
                Comments: {loanBasicInfo.ClientFeedback.General.map((note, index) => (
                  <li key = {index}>
                    {note.includes("(")?(
                      <>{note.substring(0, note.lastIndexOf("("))}</>
                    ):(<>{note}</>)}
                  </li>
                  ))}
              </p>
            )}

            {loadingLoanData &&
            <Container className = 'mx-3'>
              <Alert height = '50%' variant = 'info'>
                  Loading Loan Data...<br />
                  Do not try to edit any fields while data is loading!
                </Alert>
            </Container>
            }
            
            {!loadingLoanData &&
            <>
              <Nav variant='tabs' activeKey={tab} onSelect={(selectedKey) => setTab(selectedKey)}>
                <Nav.Item>
                    <Nav.Link eventKey = "LoanInfo">Info</Nav.Link>
                </Nav.Item>
                {loan.LoanType == "Fix and Flip" && (
                  <Nav.Item>
                    <Nav.Link eventKey = "ScopeOfWork">Scope Of Work</Nav.Link>
                  </Nav.Item>
                )}
                <Nav.Item>
                    <Nav.Link eventKey = "LoanFiles">Files</Nav.Link>
                </Nav.Item>
              </Nav>


              {tab == "LoanInfo" && (
                <LoanInfoForm
                  id = "LoanInfo"
                  loanRequInfoList = {loanRequInfoList}
                  handleRadioChange = {handleRadioChange}
                  handleTextInfoChange= {handleTextInfoChange}
                  handleClickSave = {handleClickSave}
                  onBlur = {onBlur}
                  loan = {loan}
                  saveBtnText = {saveBtnText}
                  back = {() => handleBack("LoanInfo")}
                  next = {() => handleNext("LoanInfo")}
                  stopSave = {stopSave}
                />
              )}

              {tab == "LoanFiles" && (
                <LoanFiles
                  id = "LoanFiles"
                  loanRequFileList = {loanRequFileList}
                  loanBasicInfo = {loanBasicInfo}
                  handleFileChange = {handleFileChange}
                  isUploadingFile = {isUploadingFile}
                  isSavingFile = {isSavingFile}
                  setSelectedFiles={setSelectedFiles}
                  selectedFiles = {selectedFiles}
                  isOccupied = {loan.LoanType == "Refinance" && loanInfoList['Occupancy Status'] != "Vacant"?true:false}
                  handleGetFromOtherLoan ={handleGetFromOtherLoan}
                  showModal = {showModal}
                  ClientID = {ClientID}
                  setShowModal = {setShowModal}
                  setClickedRemove = {setClickedRemove}
                  previewFileCache = {previewFileCache}
                  setPreviewFileCache = {setPreviewFileCache}
                  credentials = {credentials}
                  back = {() => setTab(loan.LoanType == "Fix and Flip"?"ScopeOfWork":"LoanInfo")}
                />
              )}

              {tab == "ScopeOfWork" && (
                <ScopeOfWorkForm 
                  id = "ScopeOfWork"
                  loanRequInfoList = {loanRequInfoList}
                  handleRadioChange = {handleRadioChange}
                  handleTextInfoChange = {handleTextInfoChange}
                  onBlur = {onBlur}
                  handleClickSave={handleClickSave}
                  saveBtnText={saveBtnText}
                  next = {() => handleNext("ScopeOfWork")}
                  back = {() => handleBack("ScopeOfWork")}
                  stopSave = {stopSave}
                />
              )}
            </>
            }
            
        </div>
    )
}

export default LoanTemplate;